<template>
    <section :class="['geo-wrapper', { compact: compact }]">
        <div class="flex-wrapper">
            <div class="geo-search-input">
                <input
                    :id="id"
                    ref="searchInput"
                    v-model="geoInput"
                    type="text"
                    :disabled="picked"
                    :placeholder="placeholder"
                    @keydown.enter="lookup"
                />
                <span
                    v-if="picked"
                    class="success icon material-symbols-outlined"
                >
                    check_circle
                </span>
            </div>
            <Bttn
                v-if="!picked"
                :id="compact ? undefined : 'geo-search-button'"
                class="outlined slim"
                :class="{ 'compact-geo-btn': compact }"
                :icon="compact ? 'search' : null"
                :disabled="isSearchDisabled"
                @click="lookup"
            >
                <span v-if="!compact">Search</span>
            </Bttn>
            <Bttn
                v-else
                :id="compact ? undefined : 'geo-clear-button'"
                class="outlined slim"
                :class="{ 'compact-geo-btn': compact }"
                :icon="compact ? 'close' : null"
                @click="clear"
            >
                <span v-if="!compact">Clear</span>
            </Bttn>
        </div>
        <ol v-if="!!options" class="geo-list">
            <li
                v-for="(item, index) in options"
                :key="item.ui_name"
                @click="pick(index)"
            >
                <span class="material-symbols-outlined">
                    {{ getIcon(item) }}
                </span>
                <span class="text">{{ item.ui_name }}</span>
            </li>
        </ol>
        <div v-if="noResults">No {{ levelPlural }} found.</div>
        <div v-if="error">{{ error }}</div>
    </section>
</template>
<script>
import { getGeos, ICON_MAP } from "js/lib/geocoding.js";
import { ServerError } from "js/lib/server.js";

const levelPluralMapping = {
    place: "places",
    city: "city, town or village",
    region: "regions",
    country: "countries",
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
    name: "GeoSearchInput",
    props: {
        id: {
            type: String,
            default: "geo-search",
        },
        modelValue: {
            type: Object,
            default: null,
        },
        placeholder: {
            type: String,
            default: "",
        },
        level: {
            type: String,
            default: undefined,
        },
        compact: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:modelValue"],
    data() {
        return {
            levelPlural: "",
            options: [],
            picked: null,
            geoInput: "",
            noResults: false,
            searching: false,
            error: "",
        };
    },
    computed: {
        isSearchDisabled() {
            return this.searching || !this.geoInput;
        },
    },
    watch: {
        modelValue() {
            this.showPicked();
        },
    },
    mounted() {
        this.levelPlural = levelPluralMapping[this.level];
        this.showPicked();
    },
    methods: {
        focus() {
            this.$refs.searchInput.focus();
        },
        showPicked() {
            this.picked = this.modelValue ?? null;
            this.geoInput = this.picked?.name ?? "";
        },
        lookup(event) {
            event.preventDefault();
            this.searching = true;
            this.$nextTick(() => {
                this.getGeosAndSet(this.$refs.searchInput.value);
            });
        },
        preload(name) {
            if (name === null) {
                this.noResults = true;
                return;
            }
            this.geoInput = name;
            this.searching = true;
            this.$nextTick(() => {
                wait(1000).then(() => {
                    this.getGeosAndSet(name);
                });
            });
        },
        async getGeosAndSet(name) {
            this.options = [];
            this.noResults = false;
            this.error = "";

            try {
                let geos = (
                    await getGeos(name, this.$enumGet("geo_level", this.level))
                ).filter(this.isValidGeo);
                if (geos.length === 1) {
                    this.geoInput = geos[0].name;
                    this.$emit("update:modelValue", geos[0]);
                } else {
                    this.options = geos;
                    this.noResults = !geos.length;
                }
            } catch (err) {
                if (err instanceof ServerError && err.status === 429) {
                    this.error = err.message;
                } else {
                    throw err;
                }
            } finally {
                wait(1000).then(() => {
                    this.searching = false;
                });
            }
        },
        isValidGeo(geo) {
            return this.level !== "city" || geo.city;
        },
        pick(index) {
            this.picked = this.options[index];
            this.options = [];
            this.geoInput = this.picked.name;
            this.$emit("update:modelValue", this.picked);
        },
        clear() {
            this.picked = null;
            this.geoInput = "";
            this.$emit("update:modelValue", this.picked);
        },
        getIcon(geo) {
            if (geo.level <= 2) {
                return "flag";
            }
            if (geo.level === 3) {
                return "location_city";
            }
            return ICON_MAP[geo.ui_type] ?? "tour";
        },
    },
};
</script>
